import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    Tooltip,
    IconButton,
    Badge,
    useMediaQuery,
    useTheme,
    Card,
    CardContent,
    Grid,
    Divider
} from '@mui/material';
import {
    Download as DownloadIcon,
    Error as ErrorIcon,
    Refresh as RefreshIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    GetApp as GetAppIcon
} from '@mui/icons-material';
import axiosWithAuth from '../../utils/axiosWithAuth';

const ContentStatusAndDownload = ({ userSubscriptions }) => {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [error, setError] = useState('');
    const [autoSyncActive, setAutoSyncActive] = useState(false);
    const tasksRef = useRef(tasks);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        // Update ref when tasks change
        tasksRef.current = tasks;
    }, [tasks]);

    useEffect(() => {
        // Initial load
        fetchContentTasks();

        // Set up auto-refresh only if there are pending tasks
        const interval = setInterval(() => {
            // Only sync if there are pending tasks
            const hasPendingTasks = tasksRef.current.some(task =>
                task.status === 'QUEUED' || task.status === 'RUNNING' || task.status === 'PENDING' || task.status === 'PROGRESS'
            );

            setAutoSyncActive(hasPendingTasks);

            if (hasPendingTasks) {
                syncTaskStatuses();
            }
        }, 30000);

        return () => clearInterval(interval);
    }, [selectedSubscription]);

    const syncTaskStatuses = async () => {
        try {
            // Check if there are pending tasks before syncing
            const hasPendingTasks = tasks.some(task =>
                task.status === 'QUEUED' || task.status === 'RUNNING' || task.status === 'PENDING' || task.status === 'PROGRESS'
            );

            if (!hasPendingTasks) {
                console.log('No pending tasks to sync');
                return;
            }

            setLoading(true);
            setError('');
            await axiosWithAuth.post('/external/sync-task-statuses');
            // Refresh tasks after sync
            await fetchContentTasks();
        } catch (error) {
            console.error('Failed to sync task statuses:', error);
            setError('Failed to sync task statuses');
        } finally {
            setLoading(false);
        }
    };

    const fetchContentTasks = async () => {
        try {
            setLoading(true);
            setError('');
            const params = selectedSubscription ? { subscription_id: selectedSubscription.id } : {};
            const response = await axiosWithAuth.get('/external/content-tasks', { params });
            setTasks(response.data);

            // Check if there are pending tasks for auto-sync indicator
            const hasPendingTasks = response.data.some(task =>
                task.status === 'QUEUED' || task.status === 'RUNNING' || task.status === 'PENDING' || task.status === 'PROGRESS'
            );
            setAutoSyncActive(hasPendingTasks);
        } catch (error) {
            console.error('Failed to fetch tasks:', error);
            setError('Failed to fetch content tasks');
        } finally {
            setLoading(false);
        }
    };

    const downloadContent = async (taskId) => {
        try {
            const response = await axiosWithAuth.get(`/external/content/${taskId}/download`, {
                responseType: 'blob'
            });

            // Create download link
            const url = window.URL.createObjectURL(response.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `content_${taskId}.zip`;
            a.click();
            window.URL.revokeObjectURL(url);

            // Refresh to update download count
            fetchContentTasks();
        } catch (error) {
            console.error('Download failed:', error);
            setError('Failed to download content');
        }
    };

    const toggleFavorite = async (taskId) => {
        try {
            await axiosWithAuth.post(`/external/content-tasks/${taskId}/favorite`);
            fetchContentTasks();
        } catch (error) {
            console.error('Failed to toggle favorite:', error);
            setError('Failed to update favorite status');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'SUCCESS':
            case 'COMPLETED':
                return 'success';
            case 'FAILURE':
            case 'FAILED':
                return 'error';
            case 'RUNNING':
            case 'PROGRESS':
                return 'warning';
            case 'QUEUED':
            case 'PENDING':
                return 'default';
            default:
                return 'default';
        }
    };

    const formatOperationType = (operationType) => {
        return operationType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    // Mobile-friendly task card component
    const TaskCard = ({ task }) => (
        <Card sx={{ mb: 2, maxWidth: '100%', overflow: 'hidden' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ wordBreak: 'break-word', overflow: 'hidden' }}>
                            {task.business_profile?.entity_name || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                            {task.task_id.slice(0, 8)}...
                        </Typography>
                    </Box>
                    <Chip
                        label={task.status}
                        size="small"
                        color={getStatusColor(task.status)}
                        sx={{ flexShrink: 0 }}
                    />
                </Box>

                <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Operation</Typography>
                        <Chip
                            label={formatOperationType(task.operation_type)}
                            size="small"
                            color="primary"
                            sx={{ wordBreak: 'break-word' }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Created</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {formatDate(task.created_at)}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Duration</Typography>
                        <Typography variant="body2">
                            {task.duration ? `${task.duration}s` : 'N/A'}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Quota Used</Typography>
                        <Typography variant="body2">
                            {task.quota_used || 0}
                        </Typography>
                    </Grid>
                </Grid>

                {task.content_summary && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">Content Summary</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word', overflow: 'hidden' }}>
                            {getContentSummary(task)}
                        </Typography>
                    </Box>
                )}

                <Divider sx={{ my: 1 }} />

                <Box display="flex" gap={1} flexWrap="wrap" justifyContent="center" sx={{ maxWidth: '100%' }}>
                    {task.download_urls && task.download_urls.length > 0 && (
                        <Tooltip title="Download Content">
                            <IconButton
                                size="medium"
                                color="primary"
                                onClick={() => downloadContent(task.task_id)}
                            >
                                <DownloadIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="Refresh Status">
                        <IconButton
                            size="medium"
                            onClick={() => fetchContentTasks()}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </CardContent>
        </Card>
    );

    const formatDuration = (seconds) => {
        if (!seconds) return 'N/A';
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        }
        return `${minutes}m`;
    };

    const getContentSummary = (task) => {
        if (!task.content_summary) return 'N/A';

        const summary = task.content_summary;
        const parts = [];

        if (summary.pages) parts.push(`${summary.pages} pages`);
        if (summary.blogs) parts.push(`${summary.blogs} blogs`);
        if (summary.images) parts.push(`${summary.images} images`);

        return parts.length > 0 ? parts.join(', ') : 'N/A';
    };

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h5">
                        Content Status & Download
                    </Typography>
                    {autoSyncActive && (
                        <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={12} />
                            Auto-sync active (checking every 30s)
                        </Typography>
                    )}
                </Box>
                <Box display="flex" gap={2}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={syncTaskStatuses}
                        disabled={loading}
                    >
                        Sync Status
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={fetchContentTasks}
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {/* Subscription Filter */}
            <FormControl sx={{ mb: 3, minWidth: 200 }}>
                <InputLabel>Filter by Subscription</InputLabel>
                <Select
                    value={selectedSubscription?.id || ''}
                    onChange={(e) => {
                        const sub = userSubscriptions.find(s => s.id === e.target.value);
                        setSelectedSubscription(sub);
                    }}
                    label="Filter by Subscription"
                >
                    <MenuItem value="">All Subscriptions</MenuItem>
                    {userSubscriptions.map(sub => (
                        <MenuItem key={sub.id} value={sub.id}>
                            {sub.subscription_name || sub.plan?.name || 'Unnamed Subscription'} - {sub.status}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* Tasks Display */}
            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                </Box>
            ) : tasks.length === 0 ? (
                <Box textAlign="center" py={4}>
                    <Typography variant="body2" color="textSecondary">
                        No content tasks found
                    </Typography>
                </Box>
            ) : isMobile ? (
                // Mobile view - Cards
                <Box>
                    {tasks.map((task) => (
                        <TaskCard key={task.id} task={task} />
                    ))}
                </Box>
            ) : (
                // Desktop view - Table
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Task ID</TableCell>
                                <TableCell>Business</TableCell>
                                <TableCell>Operation</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell>Duration</TableCell>
                                <TableCell>Quota Used</TableCell>
                                <TableCell>Downloads</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : tasks.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center">
                                        <Typography variant="body2" color="textSecondary">
                                            No content tasks found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                tasks.map((task) => (
                                    <TableRow key={task.id}>
                                        <TableCell>
                                            <Typography variant="body2" fontFamily="monospace">
                                                {task.task_id.slice(0, 8)}...
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {task.business_profile ? (
                                                <Box>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {task.business_profile.entity_name}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {task.business_profile.industry_type || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="textSecondary">
                                                    N/A
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                <Chip
                                                    label={formatOperationType(task.operation_type)}
                                                    size="small"
                                                    color="primary"
                                                />
                                                {task.content_summary && (
                                                    <Typography variant="caption" display="block" color="textSecondary">
                                                        {getContentSummary(task)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={task.status}
                                                size="small"
                                                color={getStatusColor(task.status)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2">
                                                    {formatDate(task.created_at)}
                                                </Typography>
                                                {task.completed_at && (
                                                    <Typography variant="caption" color="textSecondary">
                                                        Completed: {formatDate(task.completed_at)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {formatDuration(task.processing_duration_seconds)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={task.quota_used}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Badge badgeContent={task.download_count} color="primary">
                                                    <GetAppIcon fontSize="small" />
                                                </Badge>
                                                {task.download_count > 0 && (
                                                    <Typography variant="caption" color="textSecondary">
                                                        {formatDate(task.last_downloaded_at)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" gap={1}>
                                                {(task.status === 'SUCCESS' || task.status === 'COMPLETED') && (
                                                    <Tooltip title="Download Content">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => downloadContent(task.task_id)}
                                                        >
                                                            <DownloadIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title={task.is_favorite ? "Remove from Favorites" : "Add to Favorites"}>
                                                    <IconButton
                                                        size="small"
                                                        color={task.is_favorite ? "warning" : "default"}
                                                        onClick={() => toggleFavorite(task.task_id)}
                                                    >
                                                        {task.is_favorite ? <StarIcon /> : <StarBorderIcon />}
                                                    </IconButton>
                                                </Tooltip>
                                                {(task.status === 'FAILURE' || task.status === 'FAILED') && task.error_message && (
                                                    <Tooltip title={task.error_message}>
                                                        <IconButton size="small" color="error">
                                                            <ErrorIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {(task.status === 'RUNNING' || task.status === 'PROGRESS') && (
                                                    <CircularProgress size={20} />
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default ContentStatusAndDownload; 